import { type FC, Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import {
  ENGAGEMENT_OVERVIEW_TAB_ITEMS,
  type EngagementOverviewTabId,
} from "./EngagementOverviewTabs.data";
import "./EngagementOverviewTabs.scss";

const AllCampaignsDashboard = lazy(() => import("../allCampaigns/AllCampaignsDashboard"));
const EmailCampaignsDashboard = lazy(() => import("../emailCampaigns/EmailCampaignsDashboard"));
const WhatsAppCampaignsDashboard = lazy(
  () => import("../whatsappCampaigns/WhatsAppCampaignsDashboard"),
);

const tabDomId = (id: EngagementOverviewTabId) => `engagement-overview-tab-${id}`;
const panelDomId = (id: EngagementOverviewTabId) => `engagement-overview-panel-${id}`;

const EngagementOverviewTabs: FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<EngagementOverviewTabId>("all-campaigns");

  return (
    <div className="engagement-overview-tabs">
      <div
        className="engagement-overview-tabs__tabs"
        role="tablist"
        aria-label={t("engagementOverview.shared.tabsAriaLabel")}
      >
        {ENGAGEMENT_OVERVIEW_TAB_ITEMS.map(tab => (
          <button
            key={tab.id}
            id={tabDomId(tab.id)}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={panelDomId(tab.id)}
            className={
              activeTab === tab.id
                ? "engagement-overview-tabs__tab engagement-overview-tabs__tab--active"
                : "engagement-overview-tabs__tab"
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className="engagement-overview-tabs__content">
        <Suspense
          fallback={
            <div className="engagement-overview-tabs__loading" role="status" aria-live="polite">
              <CircularLoader size={32} thickness={4} />
            </div>
          }
        >
          <div
            id={panelDomId("all-campaigns")}
            role="tabpanel"
            aria-labelledby={tabDomId("all-campaigns")}
            hidden={activeTab !== "all-campaigns"}
          >
            {activeTab === "all-campaigns" ? <AllCampaignsDashboard /> : null}
          </div>
          <div
            id={panelDomId("email-campaigns")}
            role="tabpanel"
            aria-labelledby={tabDomId("email-campaigns")}
            hidden={activeTab !== "email-campaigns"}
          >
            {activeTab === "email-campaigns" ? <EmailCampaignsDashboard /> : null}
          </div>
          <div
            id={panelDomId("whatsapp-campaigns")}
            role="tabpanel"
            aria-labelledby={tabDomId("whatsapp-campaigns")}
            hidden={activeTab !== "whatsapp-campaigns"}
          >
            {activeTab === "whatsapp-campaigns" ? <WhatsAppCampaignsDashboard /> : null}
          </div>
        </Suspense>
      </div>
    </div>
  );
};

export default EngagementOverviewTabs;
